import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import Typography from '@material-ui/core/Typography'
import type { SuggestionData } from 'ckeditor5-premium-features'
import clsx from 'clsx'
import { PROPERTY_EDITOR_PILL_STATE_VARIANT } from 'components/WorkspaceEditor/features/pillSelection'
import { useCKEditorToggle } from 'components/WorkspaceEditor/hooks'
import { useOptionGroupStyles } from 'components/WorkspaceEditor/styles'
import React, { memo, useMemo } from 'react'
import type { ContentEditorProps } from '../ContentEditor'
import { extractSuggestions } from '../ContentEditor/contentUtils'
import { withLazyLoad } from '../withLazyComponent'
import { useFormatReplacementOption } from './hooks/useFormatReplacementOption'
import { useReplacementOptionController } from './useReplacementOptionController'

/** Constants for option item states */
const OPTION_ITEM_STATE = {
    INACTIVE: 'InActive',
} as const

/** Props for the ReplacementOption component */
interface ReplacementOptionProps {
    /** The content item containing the text and metadata for the replacement option */
    contentItem: Partial<TextContentElement>

    /** Optional CSS classes to apply custom styling */
    classes?: string | Record<string, string>

    /** Whether the replacement option is in an inactive state (disabled) */
    inactive?: boolean

    /** Whether the component allows text editing */
    editable?: boolean

    /** Optional React key for list rendering optimization */
    selector?: string

    /**
     * Callback function called when the content value changes.
     * @param updatedItem - The updated content item with new values
     * @param isSelected - Whether the item is currently selected
     */
    onValueChange?: (
        updatedItem: Partial<TextContentElement>,
        isSelected: boolean
    ) => void
}

const ContentEditor = withLazyLoad<ContentEditorProps>(() =>
    import('../ContentEditor').then((module) => ({
        default: module.ContentEditor,
    }))
)

/**
 * ReplacementOption Component
 *
 * Renders a replacement option that can display both static and editable content.
 * When editable, it integrates with CKEditor for rich text editing capabilities.
 * Supports Units of Measure handling and maintains consistency with project settings.
 *
 * Features:
 * - Static text display with proper styling
 * - Rich text editing with CKEditor integration
 * - Units of Measure processing and formatting
 * - Focus management and keyboard navigation
 * - Optimistic updates with rollback capabilities
 *
 * @param props - The component props
 * @returns JSX element representing the replacement option
 */
export const ReplacementOption = memo<ReplacementOptionProps>(
    ({
        contentItem,
        classes,
        inactive = false,
        editable = false,
        selector,
        onValueChange,
    }) => {
        const {
            editableText,
            editableTextBoxed,
            disabledBoxedInputContainer,
            notDisabledBoxedInputContainer,
            replacementOptionNoBackground,
            defaultCursor,
            disabledOptionGroup,
        } = useOptionGroupStyles()

        // Initialize controller for handling editor events and state management
        const { handlers } = useReplacementOptionController(
            contentItem,
            onValueChange
        )

        // Extract content item ID for tracking and reference
        const contentItemId = contentItem.id || ''

        // Generate unique test ID for component identification in tests
        const testId = `replacement-option-${contentItem.id || 'default'}`

        // Format text content with child elements and UOM handling
        const { formattedText } = useFormatReplacementOption(contentItem)

        // Establish content display priority: formatted text > original text > empty string
        // This ensures we always have displayable content even if formatting fails
        const displayText = formattedText || contentItem.text?.trim() || ''

        // Simple HTML wrapper for CKEditor - no memoization needed for basic string template
        const initialEditorData = `<p>${displayText}</p>`

        // Extract suggestions for track changes functionality
        const initialSuggestions: SuggestionData[] = useMemo(
            () => extractSuggestions([contentItem]),
            [contentItem.id, contentItem.changeType]
        )
        // Extract option item state for disabled styling only
        const isInActiveOptionItem: boolean = useMemo(
            () =>
                contentItem?.optionItemState ===
                PROPERTY_EDITOR_PILL_STATE_VARIANT.InActive,
            [contentItem.optionItemState]
        )

        // Initialize CKEditor toggle functionality for focus/blur management
        const { active, placeholderRef, activate } = useCKEditorToggle(
            contentItemId
        )

        /**
         * Determine if the component should be disabled (non-interactive)
         *
         * A replacement option is disabled when any of these conditions are true:
         * - Parent paragraph is inactive (inactive=true)
         * - Component is not editable (editable=false)
         */
        const isDisabled = inactive || !editable

        return (
            <Typography
                key={selector || testId}
                data-testid={testId}
                data-suggestion={`content-suggestion-id-${initialSuggestions?.[0]?.id}`}
                tabIndex={isDisabled || isInActiveOptionItem ? -1 : 0}
                aria-disabled={isDisabled || isInActiveOptionItem}
                ref={active ? placeholderRef : null}
                onFocusCapture={isDisabled ? undefined : () => activate()}
                onClick={isDisabled ? undefined : () => activate()}
                className={clsx(
                    classes,
                    editableText,
                    editableTextBoxed,
                    isDisabled
                        ? disabledBoxedInputContainer
                        : notDisabledBoxedInputContainer,
                    replacementOptionNoBackground,
                    !editable && defaultCursor,
                    isDisabled && disabledOptionGroup
                )}
                onKeyDown={
                    isDisabled
                        ? undefined
                        : (event) => handlers.onKeyDown(event)
                }
            >
                {active && !isDisabled && editable && !isInActiveOptionItem ? (
                    <ContentEditor
                        initialData={initialEditorData}
                        initialSuggestions={initialSuggestions}
                        handlers={handlers}
                        enableTrackChange={false}
                    />
                ) : (
                    displayText
                )}
            </Typography>
        )
    }
)
